import React, { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createNotification,
  editNotification,
  sendAgainNotification,
} from "../../redux/notification/slice";
import Breadcrumb from "../../components/common/Breadcrumb";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectBox,
} from "../../components/micro";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const AddNotification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const location = useLocation();
  const notificationData = location?.state?.notification;
  const isEditMode = location?.state?.mode;

  const typeOptions = [
    { value: "morning", label: "Morning" },
    { value: "evening", label: "Evening" },
  ];

  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      title: formValues?.title,
      body: formValues?.body,
    };

    if (formValues?.type && formValues?.type.trim() !== "") {
      payload.type = formValues.type;
    }

    let action;
    if (isEditMode === "sendagain" && notificationData?._id) {
      action = sendAgainNotification({ id: notificationData._id, ...payload });
    } else if (isEditMode === "edit" && notificationData?._id) {
      action = editNotification({ id: notificationData._id, ...payload });
    } else {
      action = createNotification(payload);
    }

    dispatch(action)
      .then((res) => {
        if (
          res.type === "editNotification/fulfilled" ||
          res.type === "sendAgainNotification/fulfilled" ||
          res.type === "createNotification/fulfilled"
        ) {
          navigate("/push-notification");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  const handleCancelClick = () => navigate("/push-notification");
  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode === "sendagain"
            ? "Send Notification"
            : isEditMode === "edit"
            ? "Update Notification"
            : "Create Notification"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={
            isEditMode === "sendagain"
              ? "Send Notification"
              : isEditMode === "edit"
              ? "Update Notification"
              : "Create Notification"
          }
          breadCrumbParent3={"Push Notification"}
          breadCrumbTitleKey={"/"}
        />
      </div>
      <div className="w-full bg-bgWhite shadow rounded-lg py-6 px-6">
        <Formik
          enableReinitialize
          initialValues={{
            title: notificationData ? notificationData.title : "",
            body: notificationData ? notificationData.body : "",
            type: notificationData ? notificationData.type : "",
          }}
          validationSchema={Yup.object().shape({
            title: Yup.string()
              .trim()
              .nullable()
              .required("Please enter title"),
            body: Yup.string().trim().nullable().required("Please enter body"),
            type: Yup.string()
              .trim()
              .nullable()
              .notRequired()
              .oneOf(
                ["morning", "evening"],
                "Type must be morning or evening"
              ),
          })}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-6 mb-4">
                <InputWithLabel
                  idFromName="title"
                  label="Title*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.title}
                  errors={errors.title && touched.title}
                  className={`${
                    errors.title && touched.title
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.title}
                />
                <InputWithLabel
                  idFromName="body"
                  label="Body*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.body}
                  errors={errors.body && touched.body}
                  className={`${
                    errors.body && touched.body
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.body}
                />
              </div>
              <div className="grid grid-cols-2 gap-6 mb-4">
                <SelectBox
                  name="type"
                  placeholder="Select Type"
                  options={typeOptions}
                  value={
                    typeOptions.find(
                      (opt) => opt.value === values.type
                    ) || null
                  }
                  onChange={(option) => {
                    setFieldValue("type", option?.value ?? "");
                  }}
                  errors={errors.type && touched.type}
                  errorsText={errors.type}
                />
              </div>
              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value={"Cancel"}
                  onClick={handleCancelClick}
                />

                <DefaultButton
                  type="submit"
                  value={
                    isEditMode === "sendagain"
                      ? "Send Notification"
                      : isEditMode === "edit"
                      ? "Update"
                      : "confirm"
                  }
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={
            isEditMode === "sendagain"
              ? "Send Notification"
              : isEditMode === "edit"
              ? "Update"
              : "Create"
          }
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddNotification;
